import { BarPlot } from '@mui/x-charts/BarChart';
import { ChartsTooltip } from '@mui/x-charts/ChartsTooltip';
import { ChartsXAxis } from '@mui/x-charts/ChartsXAxis';
import { ChartsYAxis } from '@mui/x-charts/ChartsYAxis';
import { ChartsContainer } from '@mui/x-charts/ChartsContainer';
import { INVOICE_REVIEW_STATUSES, InvoiceReviewStatus } from '@vy/types';
import { formatNumber, getObjectPropertyOrDefault, isNumber } from '@vy/utils';
import { sumBy } from 'lodash';
import { useMemo } from 'react';
import { useContainerWidth } from '../../../../../hooks/use-container-width';
import {
  PRODUCT_COLOR_PALETTE as COLOR_PALETTE,
  getBarChartColors,
} from '../../../../../utils/charts/bar-chart/get-bar-chart-colors';
import { formatNumberAbbreviated } from '../../../../../utils/number';

export type RevenueItem = {
  name: string;
  invoicingDate: string;
  status: InvoiceReviewStatus;
  totalRevenue: number;
  uniqueKey: string;
};

type BillingScheduleChartProps = {
  revenueData: RevenueItem[];
  chartData: Record<string, string | number>[];
};

const DEFAULT_TRIM_PIXELS = 12;
const MAX_TRIM_RATIO = 0.3;
const FALLBACK_CHART_WIDTH = 800;

export const BillingScheduleChart: React.FC<BillingScheduleChartProps> = ({ revenueData, chartData }) => {
  const [containerRef, containerWidth] = useContainerWidth<HTMLDivElement>();

  const seriesData = useMemo(() => {
    const productsWithRevenue = filterProductsWithRevenue(revenueData, chartData);
    const colors = getColorsRecord(productsWithRevenue);

    const seriesData = productsWithRevenue.map((product, index) => {
      const colorKey = buildColorKey(product, productsWithRevenue);

      const productColor = getObjectPropertyOrDefault(colors, colorKey, getBarChartColors(index));

      const color =
        product.status !== INVOICE_REVIEW_STATUSES.Approved
          ? `url(#DiagonalStripes${COLOR_PALETTE.indexOf(productColor) + 1})`
          : productColor;

      return {
        type: 'bar' as const,
        dataKey: product.uniqueKey,
        id: product.uniqueKey,
        label: product.name,
        color,
        stack: 'total',
        valueFormatter: ((value: number) => (value === 0 ? null : formatNumber(value))) as any,
      };
    });

    return seriesData;
  }, [revenueData, chartData]);

  const clipTrim = useMemo(() => {
    const chartWidth = containerWidth || FALLBACK_CHART_WIDTH;
    const estimatedBarWidth = chartWidth / Math.max(chartData.length, 1);
    const maxAllowedTrim = estimatedBarWidth * MAX_TRIM_RATIO;

    return Math.min(DEFAULT_TRIM_PIXELS, Math.floor(maxAllowedTrim));
  }, [chartData.length, containerWidth]);

  return (
    <div ref={containerRef}>
      <ChartsContainer
        dataset={chartData}
        xAxis={[
          {
            scaleType: 'band',
            dataKey: 'monthYear',
            tickLabelStyle: {
              fontSize: 12,
            },
          },
        ]}
        yAxis={[
          {
            tickLabelStyle: {
              fontSize: 12,
            },
            valueFormatter: (value: number | null) => formatNumberAbbreviated(value ?? 0),
          },
        ]}
        series={seriesData}
        height={450}
        margin={{ left: 40 }}
      >
        <BarPlot
          slotProps={{
            bar: {
              clipPath: `inset(0px ${clipTrim}px 0px ${clipTrim}px)`,
            },
          }}
        />
        <ChartsXAxis />
        <ChartsYAxis />
        <ChartsTooltip trigger="item" />
        {COLOR_PALETTE.map((color, index) => (
          <pattern
            key={index}
            id={`DiagonalStripes${index + 1}`}
            patternUnits="userSpaceOnUse"
            width="8"
            height="8"
            patternTransform="rotate(45)"
          >
            <rect x="0" y="0" width="100%" height="100%" fill={color} />
            <line x1="0" y="0" x2="0" y2="8" stroke="#ffffff" strokeWidth="2" />
          </pattern>
        ))}
      </ChartsContainer>
    </div>
  );
};

function filterProductsWithRevenue(
  revenueItems: RevenueItem[],
  chartData: Record<string, string | number>[],
): RevenueItem[] {
  return revenueItems.filter((product) => {
    const totalRevenue = sumBy(chartData, (item) => {
      const value = getObjectPropertyOrDefault(item, product.uniqueKey, 0);

      return isNumber(value) ? value : 0;
    });

    return totalRevenue > 0;
  });
}

function getColorsRecord(products: RevenueItem[]): Record<string, string> {
  return products.reduce<Record<string, string>>((colors, product) => {
    const colorKey = buildColorKey(product, products);

    const sameInvoicingDateAmount = products.filter(
      (currentProduct) => currentProduct.invoicingDate === product.invoicingDate,
    );

    const indexOfProduct = sameInvoicingDateAmount.findIndex(
      (currentProduct) => currentProduct.name === product.name,
    );

    colors[colorKey] = getBarChartColors(indexOfProduct);

    return colors;
  }, {});
}

function buildColorKey(product: RevenueItem, products: RevenueItem[]): string {
  const sameInvoicingDateAmount = products.filter(
    (currentProduct) => currentProduct.invoicingDate === product.invoicingDate,
  ).length;

  return sameInvoicingDateAmount > 1
    ? `${sameInvoicingDateAmount}-${product.name}`
    : sameInvoicingDateAmount.toString();
}
